// Live filter thumbnails (§11.19): her photo with each filter, rendered at 160 px from the preview texture
// by the viewer's own renderer (no extra decode). Re-rendered when the photo or its non-filter look changes.
import { useEffect, useRef, useState } from 'react';
import { viewerHooks } from '../canvas/Viewer';
import { useEditor } from '../../state/editor';
import { useLibrary } from '../../state/library';
import { useSimple } from '../../state/simple';
import { useLang, useT, type MessageKey } from '../../i18n';
import {
  applyFilter,
  FILTER_GROUPS,
  FOOD_TABS,
  foodTab,
  SIMPLE_FILTERS,
  type FilterGroup,
  type FoodTab,
} from '../../features/simpleFilters';
import { chooseFilter, setFilterStrength } from '../../features/simpleActions';
import { fullOutputSize } from '../../engine/geometry';
import type { Settings } from '../../model/types';
import { defaultSettings } from '../../model/defaults';

const GROUP_LABEL: Record<FilterGroup, MessageKey> = {
  food: 'fgFood',
  everyday: 'fgEveryday',
  mood: 'fgMood',
  people: 'fgPeople',
  bw: 'fgBw',
};
/** Tabs inside Food: the original 10 food filters and the Mega Food Pack's sections. */
export const FOOD_TAB_LABEL: Record<FoodTab, MessageKey> = {
  originals: 'foodOriginals',
  fix: 'foodSecFix',
  dish: 'foodSecDish',
  style: 'foodSecStyle',
  menu: 'foodSecMenu',
};
export const THUMB = 160;
const DEFAULTS = defaultSettings();
/** Timing of the last full thumbnail render, for the §11.19 acceptance test and the benchmark. */
export const thumbStats = { lastMs: 0, count: 0 };

function renderThumbs(base: Settings, canvases: Map<string, HTMLCanvasElement>): boolean {
  const c = viewerHooks.controller;
  const src = c?.engine.source;
  if (!c || !src) return false;
  const t0 = performance.now();
  const full = fullOutputSize(base.geometry, src.width, src.height);
  const k = THUMB / Math.max(full.width, full.height);
  const w = Math.max(1, Math.round(full.width * k)),
    h = Math.max(1, Math.round(full.height * k));
  const out = {
    space: 'srgb' as const,
    flatten: true,
    fill: [0.12, 0.12, 0.12] as [number, number, number],
    clipping: false,
  };
  const px = new Uint8ClampedArray(w * h * 4);
  for (const f of SIMPLE_FILTERS) {
    const cv = canvases.get(f.id);
    if (!cv) continue;
    const s = applyFilter(base, f, 1);
    const d = c.engine.readOutput(c.engine.renderPreview(s, w, h, undefined, { superset: true }), out);
    for (let i = 0; i < d.length; i += 4) {
      px[i] = d[i]! * 255;
      px[i + 1] = d[i + 1]! * 255;
      px[i + 2] = d[i + 2]! * 255;
      px[i + 3] = 255;
    }
    cv.width = w;
    cv.height = h;
    cv.getContext('2d')!.putImageData(new ImageData(px, w, h), 0, 0);
  }
  thumbStats.lastMs = performance.now() - t0;
  thumbStats.count = canvases.size;
  c.request(); // the viewer's targets were borrowed: redraw the main view
  return true;
}

export function FilterGrid() {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const id = useLibrary((s) => s.activeId);
  // An unedited photo has no entry yet: fall back to the defaults (a stable object).
  const settings = useEditor((s) => (id ? s.settings[id] : undefined)) ?? (id ? DEFAULTS : undefined);
  const rec = useSimple((s) => (id ? s.filter[id] : undefined));
  const [group, setGroup] = useState<FilterGroup>('food');
  const [sub, setSub] = useState<FoodTab>('originals');
  const canvases = useRef(new Map<string, HTMLCanvasElement>());
  const [ready, setReady] = useState(0);
  const layoutReady = !!viewerHooks.controller?.layout;

  // The look the thumbnails start from: without the filter that is currently applied.
  const base: Settings | undefined =
    rec && settings && JSON.stringify(rec.applied) === JSON.stringify(settings) ? rec.base : settings;
  const baseKey = JSON.stringify(base ?? null);
  useEffect(() => {
    if (!base) return;
    const timer = window.setTimeout(() => {
      if (!renderThumbs(base, canvases.current)) setReady((n) => n + 1); // retry until the viewer is ready
    }, 120);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, baseKey, group, ready, layoutReady]);

  // Compile the thumbnail shader while the user is still looking at the photo (one program for all 33).
  useEffect(() => {
    if (layoutReady) viewerHooks.controller?.engine.prewarmSuperset();
  }, [layoutReady]);

  const strength = rec ? Math.round(rec.strength * 100) : 100;
  return (
    <div data-testid="filter-grid">
      <div role="tablist" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
        {FILTER_GROUPS.map((g) => (
          <button
            key={g}
            role="tab"
            aria-selected={group === g}
            onClick={() => setGroup(g)}
            style={group === g ? { background: 'var(--accent)', color: '#fff' } : undefined}
          >
            {t(GROUP_LABEL[g])}
          </button>
        ))}
      </div>
      {group === 'food' && (
        <div
          role="tablist"
          style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6, fontSize: 12 }}
        >
          {FOOD_TABS.map((ft) => (
            <button
              key={ft}
              role="tab"
              data-testid={`food-tab-${ft}`}
              aria-selected={sub === ft}
              onClick={() => setSub(ft)}
              style={sub === ft ? { outline: '2px solid var(--accent)' } : undefined}
            >
              {t(FOOD_TAB_LABEL[ft])}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
        <button
          data-testid="filter-none"
          onClick={() => chooseFilter(null)}
          aria-pressed={!rec}
          style={{ minHeight: 48, ...(!rec ? { outline: '2px solid var(--accent)' } : {}) }}
        >
          {t('noFilter')}
        </button>
        {SIMPLE_FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`filter-${f.id}`}
            aria-pressed={rec?.id === f.id}
            onClick={() => chooseFilter(f.id, rec ? rec.strength : 1)}
            style={{
              display: f.group === group && (group !== 'food' || foodTab(f) === sub) ? 'flex' : 'none',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              padding: 3,
              minHeight: 48,
              ...(rec?.id === f.id ? { outline: '2px solid var(--accent)' } : {}),
            }}
          >
            <canvas
              ref={(el) => {
                if (el) canvases.current.set(f.id, el);
                else canvases.current.delete(f.id);
              }}
              style={{ width: '100%', aspectRatio: '3 / 2', objectFit: 'contain', background: '#1e1e1e' }}
            />
            <span style={{ fontSize: 11 }}>{lang === 'es' ? f.nameEs : f.name}</span>
          </button>
        ))}
      </div>
      {rec && (
        <label style={{ display: 'block', marginTop: 8, fontSize: 13 }}>
          {t('strength')} {strength}%
          <input
            type="range"
            min={0}
            max={100}
            value={strength}
            data-testid="filter-strength"
            style={{ width: '100%' }}
            onChange={(e) => setFilterStrength(Number(e.target.value) / 100, false)}
            onPointerUp={(e) => setFilterStrength(Number((e.target as HTMLInputElement).value) / 100, true)}
            onKeyUp={(e) => setFilterStrength(Number((e.target as HTMLInputElement).value) / 100, true)}
          />
        </label>
      )}
    </div>
  );
}
